import { useState, useMemo } from 'react'
import { buildKey, parseKey } from '../utils/basketKeys'

// Basket state is `{ compositeKey: quantity }`. A key is a bare menu-item id
// (no add-ons) or `"<itemId>:<addOnId>+<addOnId>"` — see utils/basketKeys.js.
export const useBasket = (initialBasket = {}, menuItems = [], basketItems = {}) => {
  const [basket, setBasket] = useState(initialBasket)

  // Look up base menu items by their (plain) id, drawing from both the enriched
  // basket (items not on this page) and the current page's list. Keyed by the
  // base item id so a composite key's parsed menuItemId resolves either source.
  const allItemsById = useMemo(() => {
    const map = new Map()
    Object.values(basketItems).forEach(item => {
      if (item && item.id) map.set(item.id, item)
    })
    menuItems.forEach(item => map.set(item.id, item))
    return map
  }, [basketItems, menuItems])

  // Unit price for a basket key = base item price + chosen add-on deltas.
  const unitPriceForKey = (key) => {
    const { menuItemId, addOnIds } = parseKey(key)
    const item = allItemsById.get(menuItemId)
    if (!item) return 0
    const addOns = item.add_ons || []
    const addOnTotal = addOnIds.reduce((sum, id) => {
      const addOn = addOns.find(a => a.id === id)
      return sum + (addOn ? addOn.price_pence : 0)
    }, 0)
    return item.price_pence + addOnTotal
  }

  const addToBasket = (item, addOnIds = []) => {
    const key = buildKey(item.id, addOnIds)
    setBasket(prev => ({
      ...prev,
      [key]: (prev[key] || 0) + 1
    }))
  }

  const removeFromBasket = (key) => {
    setBasket(prev => {
      const updated = { ...prev }
      if (updated[key] > 1) {
        updated[key] -= 1
      } else {
        delete updated[key]
      }
      return updated
    })
  }

  // Set an exact quantity for a composite key (used by basket quantity steppers).
  const updateQuantity = (key, newQuantity) => {
    setBasket(prev => {
      const updated = { ...prev }
      if (newQuantity <= 0) {
        delete updated[key]
      } else {
        updated[key] = newQuantity
      }
      return updated
    })
  }

  const basketTotal = useMemo(() => {
    return Object.entries(basket).reduce((sum, [key, quantity]) => {
      return sum + unitPriceForKey(key) * quantity
    }, 0)
  }, [basket, allItemsById])

  const basketCount = useMemo(() => {
    return Object.values(basket).reduce((sum, quantity) => sum + quantity, 0)
  }, [basket])

  const clearBasket = () => {
    setBasket({})
  }

  return {
    basket,
    setBasket,
    addToBasket,
    removeFromBasket,
    updateQuantity,
    basketTotal,
    basketCount,
    clearBasket
  }
}
